import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { signInWithGoogle, isOAuthConfigured } from '../../services/oauthService';
import AuthPage from './AuthPage';

const RoleChoice = ({ value, onChange, disabled }) => (
  <div>
    <p className="mb-2 text-sm font-medium text-m-ink">I am a</p>
    <div className="grid grid-cols-2 gap-3">
      {[
        { id: 'client', title: 'Client', hint: 'Looking for vendors' },
        { id: 'vendor', title: 'Vendor', hint: 'Offering services' },
      ].map((role) => {
        const selected = value === role.id;
        return (
          <button
            key={role.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(role.id)}
            className={`rounded-2xl border px-3 py-3.5 text-center transition-colors disabled:opacity-50 ${
              selected
                ? 'border-m-ink bg-m-surface'
                : 'border-m-border-light bg-m-surface hover:border-m-ink/30'
            }`}
          >
            <span className="block text-base font-medium text-m-ink">{role.title}</span>
            <span className="mt-0.5 block text-sm text-m-ink-muted">{role.hint}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const GoogleMark = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const AppleMark = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M16.37 12.72c.03 3.18 2.79 4.24 2.82 4.25-.02.07-.44 1.51-1.45 2.99-.87 1.28-1.78 2.55-3.2 2.58-1.4.03-1.85-.83-3.45-.83s-2.11.8-3.43.86c-1.38.05-2.43-1.38-3.31-2.65-1.8-2.6-3.18-7.35-1.33-10.55.92-1.59 2.56-2.6 4.34-2.62 1.35-.03 2.63.91 3.45.91.82 0 2.35-1.12 3.96-.96.67.03 2.57.27 3.79 2.05-.1.06-2.26 1.32-2.19 3.97zM14.3 6.3c.72-.87 1.2-2.08 1.07-3.3-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-.09 3.1 1.15.09 2.32-.58 2.04-1.36z" />
  </svg>
);

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [oauthLoading, setOAuthLoading] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState('client');
  const selectedUserTypeRef = useRef('client');
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const digitRefs = useRef([]);
  const verifyingRef = useRef(false);

  useEffect(() => {
    selectedUserTypeRef.current = selectedUserType;
  }, [selectedUserType]);

  useEffect(() => {
    if (twoFactorChallenge && !useRecoveryCode) {
      digitRefs.current[0]?.focus();
    }
  }, [twoFactorChallenge, useRecoveryCode]);

  const { login, verifyTwoFactorLogin, oauthLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const nextPath = searchParams.get('next');
  const from =
    (nextPath && nextPath.startsWith('/') ? nextPath : null) ||
    location.state?.from?.pathname ||
    '/';
  const [sessionNotice, setSessionNotice] = useState('');
  const oauthConfig = isOAuthConfigured();

  useEffect(() => {
    let notice = '';
    if (searchParams.get('session') === 'expired') {
      notice = 'Your session timed out. Sign in again to continue.';
    }
    try {
      const raw = sessionStorage.getItem('auth_session_expired_notice');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.message) notice = parsed.message;
        sessionStorage.removeItem('auth_session_expired_notice');
      }
    } catch {
      // ignore
    }
    if (notice) setSessionNotice(notice);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearTwoFactorInput = () => {
    setTwoFactorCode('');
    setCodeDigits(['', '', '', '', '', '']);
    setUseRecoveryCode(false);
  };

  const focusDigit = (index) => {
    digitRefs.current[index]?.focus();
  };

  const applyDigits = (next, { submit = false } = {}) => {
    const code = next.join('');
    setCodeDigits(next);
    setTwoFactorCode(code);
    if (submit && code.length === 6) {
      submitTwoFactorCode(code);
    }
  };

  const handleDigitChange = (index, value) => {
    const chars = value.replace(/\D/g, '');
    if (chars.length > 1) {
      const next = [...codeDigits];
      chars.slice(0, 6 - index).split('').forEach((char, offset) => {
        next[index + offset] = char;
      });
      const filled = next.every(Boolean);
      applyDigits(next, { submit: filled });
      focusDigit(Math.min(index + chars.length, 5));
      return;
    }
    const next = [...codeDigits];
    next[index] = chars;
    const isLastDigit = Boolean(chars) && index === 5 && next.every(Boolean);
    applyDigits(next, { submit: isLastDigit });
    if (chars && index < 5) focusDigit(index + 1);
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const next = [...codeDigits];
      next[index - 1] = '';
      applyDigits(next);
      focusDigit(index - 1);
    }
    if (event.key === 'ArrowLeft' && index > 0) focusDigit(index - 1);
    if (event.key === 'ArrowRight' && index < 5) focusDigit(index + 1);
  };

  const handleDigitPaste = (event) => {
    const text = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    event.preventDefault();
    const next = ['', '', '', '', '', ''];
    text.split('').forEach((char, index) => {
      next[index] = char;
    });
    applyDigits(next, { submit: next.every(Boolean) });
    focusDigit(Math.min(text.length, 5));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const result = await login(formData);
      if (result.requiresTwoFactor) {
        setTwoFactorChallenge({
          token: result.challengeToken,
          email: result.email || formData.email,
        });
        clearTwoFactorInput();
        return;
      }
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitTwoFactorCode = async (code) => {
    const value = String(code || '').trim();
    if (verifyingRef.current || !twoFactorChallenge?.token || !value) return;
    verifyingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const result = await verifyTwoFactorLogin({
        challengeToken: twoFactorChallenge.token,
        code: value,
      });
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Invalid authentication code');
        if (!useRecoveryCode) {
          setCodeDigits(['', '', '', '', '', '']);
          setTwoFactorCode('');
          focusDigit(0);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      verifyingRef.current = false;
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = (e) => {
    e.preventDefault();
    submitTwoFactorCode(useRecoveryCode ? twoFactorCode : codeDigits.join(''));
  };

  const handleGoogleSignIn = async () => {
    if (oauthLoading || loading) return;
    setOAuthLoading(true);
    setError('');
    try {
      const oauthData = await signInWithGoogle();
      const result = await oauthLogin(oauthData, selectedUserTypeRef.current, 'login');
      if (result.requiresTwoFactor) {
        setTwoFactorChallenge({
          token: result.challengeToken,
          email: result.email || oauthData.email,
        });
        clearTwoFactorInput();
        return;
      }
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(typeof result.error === 'string' ? result.error : 'Google sign in failed');
      }
    } catch (err) {
      setError(err.message || 'Google sign in failed. Please try again.');
    } finally {
      setOAuthLoading(false);
    }
  };

  return (
    <AuthPage>
      <h1 className="marketing-display-md text-center text-m-ink">Sign in</h1>
      <p className="marketing-body mt-2 text-center text-m-ink-muted">Welcome back</p>
      {sessionNotice ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {sessionNotice}
        </div>
      ) : null}

      <div className="mt-8">
        {twoFactorChallenge ? (
          <form className="space-y-4" onSubmit={handleTwoFactorSubmit}>
            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            )}
            <div className="rounded-2xl border border-m-border-light bg-m-surface px-4 py-3 text-sm text-m-ink-muted">
              <div className="flex items-start gap-2">
                <Shield className="mt-0.5 h-5 w-5 shrink-0 text-m-ink" />
                <p>
                  {useRecoveryCode
                    ? 'Enter one of your recovery codes'
                    : 'Enter the 6-digit code from your authenticator'}
                  {twoFactorChallenge.email ? ` for ${twoFactorChallenge.email}` : ''}.
                </p>
              </div>
            </div>
            {useRecoveryCode ? (
              <input
                id="twoFactorCode"
                type="text"
                autoComplete="one-time-code"
                required
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder="Recovery code"
                className="auth-input"
              />
            ) : (
              <div className="grid grid-cols-6 gap-2" onPaste={handleDigitPaste}>
                {codeDigits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(node) => {
                      digitRefs.current[index] = node;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={index === 0 ? 6 : 1}
                    aria-label={`Digit ${index + 1}`}
                    value={digit}
                    disabled={loading}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(index, e)}
                    className="h-14 w-full rounded-xl border border-m-border-light bg-m-surface text-center text-lg font-medium text-m-ink outline-none focus:border-m-ink"
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setUseRecoveryCode((current) => !current);
                setTwoFactorCode('');
                setCodeDigits(['', '', '', '', '', '']);
                setError('');
              }}
              className="text-sm font-medium text-m-ink-muted underline underline-offset-2 hover:text-m-ink"
            >
              {useRecoveryCode ? 'Use authenticator code' : 'Use a recovery code'}
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                (!useRecoveryCode && twoFactorCode.length < 6) ||
                (useRecoveryCode && !twoFactorCode.trim())
              }
              className="marketing-btn marketing-btn-ink w-full"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => {
                setTwoFactorChallenge(null);
                clearTwoFactorInput();
                setError('');
              }}
              className="w-full text-sm font-medium text-m-ink-muted hover:text-m-ink"
            >
              Back to sign in
            </button>
          </form>
        ) : (
          <>
            <RoleChoice
              value={selectedUserType}
              onChange={setSelectedUserType}
              disabled={loading || oauthLoading}
            />

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setError('Apple sign-in is coming soon.')}
                className="auth-btn-outline"
              >
                <AppleMark />
                Apple
              </button>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={oauthLoading || loading || !oauthConfig.google}
                className="auth-btn-outline"
              >
                {oauthLoading ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-m-border-light border-t-m-ink" />
                ) : (
                  <GoogleMark />
                )}
                Google
              </button>
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-m-border-light" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-m-canvas-light px-3 text-m-ink-faint">or</span>
              </div>
            </div>

            <form className="space-y-3" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email"
                className="auth-input"
              />
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Password"
                  className="auth-input pr-11"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-m-ink-faint hover:text-m-ink"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-m-ink-muted underline underline-offset-2 hover:text-m-ink"
                >
                  Forgot password
                </Link>
              </div>
              <p className="text-sm text-m-ink-muted">
                No account yet?{' '}
                <Link
                  to="/register"
                  className="font-medium text-m-ink underline underline-offset-2"
                >
                  Sign up
                </Link>
              </p>
              <button
                type="submit"
                disabled={loading}
                className="marketing-btn marketing-btn-ink w-full"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </>
        )}
      </div>
    </AuthPage>
  );
};

export default Login;
